import {ConfigObjectType, LogUtils, useApiQuery} from '@wallaby-ui/core';
import {KeycloakTokenParsed} from 'keycloak-js';
import {useEffect, useMemo} from 'react';

import {constants, HaidiSchema, keycloakTokenParsed, MerieSchema, WallabyCoreSchema} from 'src/js/common';

enum DepartmentPermissionsTypeEnum {
  LIMITED_PERMISSIONS = 'LIMITED_PERMISSIONS',
  UNLIMITED_PERMISSIONS = 'UNLIMITED_PERMISSIONS',
}
type OrgLevelType = WallabyCoreSchema.components['schemas']['OrganizationLevelTypeEnum'];
type DepartmentRoles = {
  LIMITED_PERMISSIONS: boolean;
  UNLIMITED_PERMISSIONS: boolean;
  MULTI_HOSPITAL: boolean;
};
type UserRoleKey =
  | 'HAI_REPORT_VIEW_ROLE' // HAIDI
  | 'HAI_EDIT_ROLE' // HAIDI
  | 'HAI_VIEW_ROLE' // HAIDI
  | 'ATB_REPORT_VIEW_ROLE' // HAIDI
  | 'ENCOUNTER_DETAIL_VIEW_ROLE' // MERIE
  | 'ENCOUNTER_DETAIL_EDIT_ROLE' // MERIE
  | 'HOSPITALIZATIONS_VIEW_ROLE' // MERIE
  | 'RECOMMENDATION_PUBLISH_ROLE' // MERIE
  | 'GLOBAL_FILTER_EDIT_ROLE'; // HAIDI + MERIE (Global filter management permission, IDEA-283)
type UserRoles = {[k in UserRoleKey]?: boolean};
type PermissionsType = {
  isLoading: boolean;
  departmentRoles: DepartmentRoles;
  departmentRoleStrings: string[];
  departments: string[];
  userRoles: UserRoles;
  userRoleStrings: string[];
  allRoleStrings: string[];
  keycloakTokenParsed: {current: KeycloakTokenParsed | undefined};
  haiByOrganizationReportLevel: OrgLevelType;
};
export function usePermissions(config: ConfigObjectType, logPermissions = false): PermissionsType {
  // department roles
  const permissionsUrl = config.service.url.permissions;
  const {data: permissionsData, isLoading: permissionsIsLoading} = useApiQuery<
    HaidiSchema.components['schemas']['UserPermissionsDto'] | MerieSchema.components['schemas']['UserPermissionsDto']
  >([permissionsUrl], null, {
    axiosConfig: {
      url: permissionsUrl,
    },
  });
  const {data: organizations, isLoading: organizationsIsLoading} = useOrganizationsByOrgSecurity(config);
  const departmentRoles = useMemo(() => {
    const unlimitedDepartmentView = permissionsData?.departmentPermissionsType === DepartmentPermissionsTypeEnum.UNLIMITED_PERMISSIONS;
    const haveGroupOrganization = organizations.some((v) => v.levelType === 'GROUP');
    const hospitalCount = organizations.filter((v) => v.levelType === 'HOSPITAL').length;
    const acc: DepartmentRoles = {
      UNLIMITED_PERMISSIONS: !permissionsIsLoading && unlimitedDepartmentView,
      LIMITED_PERMISSIONS: !permissionsIsLoading && !unlimitedDepartmentView,
      MULTI_HOSPITAL: haveGroupOrganization && hospitalCount >= 2,
    };
    return acc;
  }, [permissionsIsLoading, permissionsData?.departmentPermissionsType, organizations]);
  const haiByOrganizationReportLevel = useMemo(() => {
    const haveGroupOrganization = organizations.some((v) => v.levelType === 'GROUP');
    const hospitalCount = organizations.filter((v) => v.levelType === 'HOSPITAL').length;
    const parentDepartmentCount = organizations.filter((v) => v.levelType === 'PARENT_DEPARTMENT').length;
    return haveGroupOrganization && hospitalCount >= 2 ? 'HOSPITAL' : parentDepartmentCount >= 2 ? 'PARENT_DEPARTMENT' : 'DEPARTMENT';
  }, [organizations]);
  const departmentRoleStrings = useMemo(() => {
    return Object.entries(departmentRoles)
      .filter(([_, v]) => v)
      .map(([k, _]) => `DEPARTMENT:${k}`);
  }, [departmentRoles]);
  const departments = useMemo(() => permissionsData?.departmentPermissionNames ?? [], [permissionsData?.departmentPermissionNames]);
  // user roles
  const userRoleStrings = useMemo(() => permissionsData?.roles ?? [], [permissionsData?.roles]);
  const userRoles = useMemo(() => {
    return Object.fromEntries(userRoleStrings.map((k) => [k, true]));
  }, [userRoleStrings]);
  // all roles
  const allRoleStrings = useMemo(() => {
    return [...departmentRoleStrings, ...userRoleStrings];
  }, [departmentRoleStrings, userRoleStrings]);
  const isLoading = permissionsIsLoading || organizationsIsLoading;
  useEffect(() => {
    if (!isLoading && logPermissions) console.log('allRoleStrings', allRoleStrings);
  }, [isLoading, logPermissions, allRoleStrings]);
  return {
    isLoading,
    departmentRoles,
    departmentRoleStrings,
    departments,
    userRoles,
    userRoleStrings,
    allRoleStrings,
    keycloakTokenParsed,
    haiByOrganizationReportLevel,
  };
}

type Organization = WallabyCoreSchema.components['schemas']['OrganizationFilterDto'];
type OrganizationsReturnValue<Org> = {
  data: Org[];
  isLoading: boolean;
  error: unknown;
};
const logger = LogUtils.getLogger('usePermissions', constants.LIBRARY_NAME);
export function useOrganizations<Org = Organization>(config: ConfigObjectType, enabled = true): OrganizationsReturnValue<Org> {
  const apiUrl = config.service.url.organizationsFilterData;
  const {data, isLoading, error} = useApiQuery<Org[]>([apiUrl], null, {
    axiosConfig: {url: apiUrl},
    reactQueryOptions: {enabled},
  });
  useEffect(() => {
    if (error) {
      logger.warn(`Error when loading organizations!\n ${error}`);
    }
  }, [error]);
  return {data: data ?? [], isLoading, error};
}
export function useOrganizationsByOrgSecurity<Org = Organization>(config: ConfigObjectType, enabled = true): OrganizationsReturnValue<Org> {
  const apiUrl = config.service.url.organizationsFilterDataByOrgSecurity;
  const {data, isLoading, error} = useApiQuery<Org[]>([apiUrl], null, {
    axiosConfig: {url: apiUrl},
    reactQueryOptions: {enabled},
  });
  useEffect(() => {
    if (error) {
      logger.warn(`Error when loading organizations!\n ${error}`);
    }
  }, [error]);
  return {data: data ?? [], isLoading, error};
}
/*
export function useOrganizationParentMap<Org = Organization>(config: ConfigObjectType, enabled = true) {
  const {data, error, isLoading} = useOrganizationsByOrgSecurity<Org>(config, enabled);
  const organizationParentMap = useMemo(() => {
    const accParentMap: Record<number, Org> = {};
    (data ?? []).forEach((v) => {
      accParentMap[(v as any).id] = v;
    });
    return accParentMap;
  }, [data]);
  return {data, organizationParentMap, error, isLoading};
}
*/

export function useInstancePublishEnabled(config: ConfigObjectType) {
  const apiUrl = config.service.url.instancePublishEnabled;
  const {data: instancePublishEnabledRaw, isLoading: instancePublishEnabledLoading} = useApiQuery<boolean>([apiUrl], null, {
    axiosConfig: {url: apiUrl},
  });
  const instancePublishEnabled = Boolean(instancePublishEnabledRaw ?? false);
  return {instancePublishEnabled, instancePublishEnabledLoading};
}
export function useInstanceLocation(config: ConfigObjectType) {
  const apiUrl = config.service.url.instanceLocation;
  const {data: instanceLocation, isLoading: instanceLocationLoading} = useApiQuery<string>([apiUrl], null, {
    axiosConfig: {url: apiUrl},
  });
  return {instanceLocation, instanceLocationLoading};
}
export function useHaidiGroupDeptsBySecurity(config: ConfigObjectType) {
  // HAIDI-only instance flag; the URL is undefined in other apps, so the query is disabled there and the flag defaults to false.
  const apiUrl = config.service.url.haidiGroupDeptsBySecurity;
  const {data, isLoading: haidiGroupDeptsBySecurityLoading} = useApiQuery<boolean>([apiUrl], null, {
    axiosConfig: {url: apiUrl},
    reactQueryOptions: {enabled: Boolean(apiUrl)},
  });
  return {haidiGroupDeptsBySecurity: Boolean(data ?? false), haidiGroupDeptsBySecurityLoading};
}
